import { v4 as uuidv4 } from "uuid";
import type { UserInfo } from "@/features/authentication/context/auth/auth.types";
import { buildSearchAndRankBody } from "../hooks/useSearchAndRank";
import { calculateLicenseFeeFromAdjustments } from "./licenseFee";
import { parseMoney } from "@/utils/parse-money";
import {
  DEFAULT_CONTRACT_TERM,
  DEFAULT_LICENSE_FEE_MODE,
  DEFAULT_LICENSE_FEE_PERCENTAGE,
  DEFAULT_PROPOSAL_TYPE,
  type AIModeState,
  type CreateProposalBody,
} from "../types";

export function buildProposalPayload(
  state: AIModeState,
  userInfo: UserInfo | null | undefined,
): CreateProposalBody {
  const { diagnosis, location, adjustments } = state;

  const selectedSpaceIds = location?.selectedSpaceIds ?? [];
  const selectedComboIds = location?.selectedComboIds ?? [];
  const roomCombinations = location?.roomCombinations ?? [];
  // The proposal's rooms = individually-selected ∪ rooms of the selected combos.
  const proposalRoomIds = new Set(selectedSpaceIds);
  for (const combo of roomCombinations) {
    if (!selectedComboIds.includes(combo.combinationId)) continue;
    for (const r of combo.rooms) proposalRoomIds.add(r.privateRoomId);
  }
  const selectedSpaces =
    adjustments?.selectedSpaces ??
    [
      ...(location?.rankedSpaces ?? []),
      ...(location?.extraSpaces ?? []),
    ].filter((s) => proposalRoomIds.has(s.id));
  const spaceAdjustments = adjustments?.spaceAdjustments ?? [];

  const licenseFeeCalc = calculateLicenseFeeFromAdjustments(
    adjustments?.licenseFeeMode ?? DEFAULT_LICENSE_FEE_MODE,
    adjustments?.licenseFeePercentage ?? DEFAULT_LICENSE_FEE_PERCENTAGE,
    adjustments?.licenseFeeFixed ?? 0,
    spaceAdjustments,
    adjustments?.contractMonths ?? DEFAULT_CONTRACT_TERM,
  );

  const searchBody = buildSearchAndRankBody(diagnosis!, {
    latitude: location?.location?.lat ?? undefined,
    longitude: location?.location?.lng ?? undefined,
    radius_km: location?.radiusKm ?? undefined,
    // The min-price filter starts prefilled from the imported deal but is
    // user-editable in LocationStep; persist what the user left there (null
    // when cleared) instead of the stale imported hard_constraints value.
    min_budget: location ? (location.priceMin ?? null) : undefined,
  });

  return {
    ...searchBody,
    proposal_type: adjustments?.proposalType ?? DEFAULT_PROPOSAL_TYPE,
    recommendation_hash: location?.recommendationHash ?? uuidv4(),
    location_label: location?.location?.label ?? null,
    location_lat: location?.location?.lat ?? null,
    location_lng: location?.location?.lng ?? null,
    radius_km: location?.radiusKm ?? null,
    selected_office_ids: selectedSpaces.map((s) => s.id),
    recommended_space_ids: adjustments?.recommendedSpaceIds ?? [],
    credits_price: adjustments?.creditsPrice ?? 0,
    contract_months: adjustments?.contractMonths ?? DEFAULT_CONTRACT_TERM,
    proposal_validity: adjustments?.proposalValidity
      ? new Date(adjustments.proposalValidity).toISOString()
      : new Date().toISOString(),
    seller_notes: adjustments?.sellerNotes ?? "",
    current_monthly_cost: parseMoney(diagnosis?.monthlyBudget),
    space_adjustments: spaceAdjustments.map((a) => ({
      space_id: a.spaceId,
      extra_value: a.extraValue,
      margin: a.margin,
      client_price: a.clientPrice,
      credits: a.credits,
      extra_credits: a.extraCredits,
      total_credits: a.totalCredits,
      total_price: a.totalPrice,
      setup_cost: a.setupCost ?? 0,
      setup_credits: a.setupCredits,
      market_discount_pct: a.marketDiscountPct,
    })),
    selected_spaces: selectedSpaces.map((s) => ({
      ...s,
      isRecommended: (adjustments?.recommendedSpaceIds ?? []).includes(s.id),
    })),
    include_flex_credits: adjustments?.includeFlexCredits,
    flex_credits: adjustments?.includeFlexCredits
      ? adjustments?.flexCredits
      : undefined,
    show_license_fee: adjustments?.showLicenseFee,
    license_fee_mode: adjustments?.licenseFeeMode ?? DEFAULT_LICENSE_FEE_MODE,
    license_fee_percentage:
      adjustments?.licenseFeePercentage ?? DEFAULT_LICENSE_FEE_PERCENTAGE,
    license_fee_fixed: adjustments?.licenseFeeFixed ?? 0,
    license_fee_value: licenseFeeCalc.value,
    license_fee_period: licenseFeeCalc.period,
    include_setup_fee: adjustments?.includeSetupFee ?? false,
    // O backend rejeita include_setup_fee=true sem valor (422); o gate de
    // validação do AdjustmentsStep garante o preenchimento antes de chegar aqui
    setup_fee: adjustments?.includeSetupFee ? adjustments?.setupFee : undefined,
    proposal_origin: "Backoffice",
    created_by: {
      user_id: userInfo?.user?.id ?? null,
      email: userInfo?.profile?.email ?? null,
      full_name: userInfo?.user
        ? `${userInfo.user.first_name} ${userInfo.user.last_name}`.trim()
        : null,
    },
  };
}
